import type { SnowflakeCredentials } from './../types/typesSnowflake'

/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Main Snowflake Authentication Store
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { oauthCodeFlow } from '~/scripts/auth/oauth'
export const useSnowflakeAuthStore = defineStore('snowflakeAuthStore', () => {
  const router = useRouter()

  // Get Snowflake Credentials from Local Cookie if Available - Return Results if Access Code is Valid
  // This is used to persist the user's authentication state across page reloads
  const snowflakeCredentials = useCookie('snowflakeAuth', { default: () => ({} as SnowflakeCredentials), maxAge: 180 * 60 })

  // //////////////////////////////////////////////////////////////////
  // Snowflake Store Items
  // //////////////////////////////////////////////////////////////////
  // Initialized from scripts/snowflake/initializeSnowflake.ts
  // const snowflakeCredentials = ref({} as SnowflakeCredentials)
  const currentUser = ref<string>('') // Current User for Snowflake Connection
  const currentRole = ref<string>('') // Current Role for Snowflake Connection
  const currentWarehouse = ref<string>('') // Current Warehouse for Snowflake Connection
  const userRoles = ref([] as any) // All Roles for the Current User
  const userWarehouses = ref([] as any) // All Warehouses for the Current User

  // Gets Current Snowflake Authentication State
  // const snowflakeAuth = (executor: 'user' | 'system' = 'user', customConnection?: SnowflakeConnection) => snowflakeAuthFunction(executor, customConnection)

  // //////////////////////////////////////////////////////////////////
  // 💬 Snowflake Connection Authentication Function
  // //////////////////////////////////////////////////////////////////
  async function snowflakeAuth(executor: 'user' | 'system' = 'user', customConnection?: SnowflakeConnection): Promise<SnowflakeAuthentication> {
    console.log(`Executing With ${executor} | Custom Connection: ${customConnection || null}`)
    if (customConnection)
      console.log('Custom Connection:', customConnection)

    let { authType, sfId, clientId, clientSecret, redirectUri, accessToken, accessTokenExpiresAt, refreshTokenEncrypted, refreshTokenExpiresAt } = snowflakeCredentials.value

    // Check if Token Has Expired and Needs to Be Refreshed - With 10 Second Buffer
    if (!accessToken || !accessTokenExpiresAt || accessTokenExpiresAt < ((Math.round(new Date().getTime() / 1000)) + 10)) {
      console.log('Access Token Expired - Refreshing Token...')

      // Refresh Access Token from Refresh Token - If Refresh Token Expired Then Logout - User Needs to Authenticate Again
      if (!refreshTokenEncrypted || !refreshTokenExpiresAt || refreshTokenExpiresAt < ((Math.round(new Date().getTime() / 1000)) + 10)) {
        console.log('Refresh Token Expired - Logging Out...')

        router.push({ path: '/logout' })

        return { authType: undefined, sfId: undefined, accessToken: undefined }
      }
      const tokenPayload = { sfId, clientId, clientSecret, redirectUri, refreshToken: refreshTokenEncrypted }
      const refreshTokenResponse = await snowflakeToken(tokenPayload) as SnowflakeCredentials

      accessToken = refreshTokenResponse.accessToken

      // //////////////////////////////////////////////////////////////////
      // 💬 Set Cookie In Case of Browser Refresh
      // //////////////////////////////////////////////////////////////////
      // snowflakeAuth.value = JSON.stringify({ authType, sfId, clientId, clientSecret, redirectUri, accessToken, accessTokenExpiresAt, refreshTokenEncrypted, refreshTokenExpiresAt })
    }

    // TODO: Re-Implement RSA KeyPair Authentication
    // const { accountProfile, userProfile } = storeToRefs(useGlobalsStore())
    // const sfId = accountProfile?.value?.snowflakeSettings?.sfId

    // // //////////////////////////////////////////////////////////////////
    // // Using "keypair" Authentication
    // // //////////////////////////////////////////////////////////////////
    // const snowflakeConnection = customConnection ? customConnection : executor === 'user' ? userProfile.value.activeSnowflakeConnection : accountProfile.value.activeSnowflakeConnection

    // // Check if JWT Token Needs to Be Refreshed
    // const token = snowflakeConnection?.token

    return { authType, sfId, accessToken }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Snowflake Exchange and Refresh Token
  // //////////////////////////////////////////////////////////////////

  async function snowflakeToken(payload: any) {
    const { sfId, clientId, clientSecret, redirectUri, code, refreshToken, userId, isAppLogin } = payload

    // Exchange Code for Token Using Cloud Functions
    const tokenUrl = `https://${sfId}.snowflakecomputing.com/oauth/token-request`

    // If "code" is Provided - Use Code to Get Token
    // If "refreshToken" is Provided - Use Refresh Token to Get New Access Token
    const serverPayload: any = { tokenUrl, clientId, clientSecret, redirectUri }
    if (code) {
      serverPayload.grantType = 'authorization_code'
      serverPayload.token = code
    }
    else {
      serverPayload.grantType = 'refresh_token'
      serverPayload.token = refreshToken
    }
    if (isAppLogin) {
      serverPayload.isAppLogin = true
      serverPayload.userId = userId
    }
    try {
      // Get Access and Refresh Tokens from Cloud Function
      const serverResponse: SnowflakeCredentials | unknown = await useCloudFunction('app_oauth_token_flow', serverPayload)
      if (serverResponse) {
        let response = serverResponse as SnowflakeCredentials

        // If User COULD AUTHENTICATE With Snowflake But No Application Account was Found for User/Account Combination
        if (response.loginError) {
          if (typeof response.loginError !== 'string' && response.loginError.message)
            response.loginError = response.loginError.message
          if (response.loginError.includes('No Account')) {
            const { applicationName } = storeToRefs(useGlobalsStore())
            const noUserAccountError = `No ${applicationName.value} Account Was Found For ${response.loginError.replace('No Account ', '')}. Contact Your ${applicationName.value} Admin.`

            return { error: noUserAccountError }
          }

          return { error: response.loginError }
        }

        // If Error Was Returned
        if (response.error) {
          console.error(`Failed to Get Access Token From ${tokenUrl}: ${response.error}`)

          return { error: response.error }
        }

        // If Tokens Received Write to Local Storage and Docs
        if (response.accessToken) {
          // Add sfId to Response
          response.authType = 'oauth'
          response.sfId = sfId
          response.clientId = clientId
          response.clientSecret = clientSecret
          response.redirectUri = redirectUri

          // Refresh Token NOT Returned on Access Token Refresh - Get Back From Global Store
          if (!response.refreshTokenEncrypted) {
            const { refreshTokenEncrypted, refreshTokenExpiresIn, refreshTokenExpiresAt, username } = snowflakeCredentials.value

            response = { ...response, refreshTokenEncrypted, refreshTokenExpiresIn, refreshTokenExpiresAt, username }
          }

          // If Connection Successful - Write Access Token to Local Storage
          snowflakeCredentials.value = response

          // Write to Cookie
          const snowflakeAuthCookie = useCookie('snowflakeAuthTest', { default: () => ({}), maxAge: 180 * 60 })
          snowflakeAuthCookie.value = JSON.stringify(response)
          return response
        }
      }
    }
    catch (error) {
      const errorMessage = `Failed to Get Access Token: ${error}`

      console.error(errorMessage)

      return { error: errorMessage }
    }
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Get Oauth Code from Snowflake Then Exchange for Tokens
  // //////////////////////////////////////////////////////////////////
  async function snowflakeOauth(sfId: string, clientId: string, userId: string) {
    const { applicationDomain } = storeToRefs(useGlobalsStore())

    // URIs
    const redirectUri = `${applicationDomain.value}/oauth`

    // If Using Dev Oauth Settings
    if (useRuntimeConfig().public.DEV_SNOWFLAKE_OAUTH_CLIENT_ID)
      clientId = useRuntimeConfig().public.DEV_SNOWFLAKE_OAUTH_CLIENT_ID

    const authUrl = `https://${sfId}.snowflakecomputing.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}`

    // It is possible to specify "scope" which isn't used at the current time i.e. "session:role:PUBLIC_ROLE"
    const oauthScope = null

    try {
      // Get Oauth Code
      const codeResponse = await oauthCodeFlow(authUrl, redirectUri, oauthScope)
      if (codeResponse.error)
        return { error: codeResponse.error }
      const { code } = codeResponse
      // Exchange Code for Token Using Cloud Functions
      const payload = { sfId, clientId, clientSecret: null as any, redirectUri, code, userId, isAppLogin: true }

      const response = await snowflakeToken(payload) as SnowflakeCredentials

      // If Login Token Successful - Write access and refresh tokens to Store and Docs
      if (response && response.loginToken)

        // Write to Store
        snowflakeCredentials.value = response

      // Write to Cookie
      // snowflakeAuthCookie.value = JSON.stringify(response)

      return response
    }
    catch (error) {
      const errorMessage = `Failed to Get Access Token: ${error}`

      console.error(errorMessage)

      return { error: errorMessage }
    }
  }

  return {
    snowflakeCredentials,
    currentUser,
    currentRole,
    currentWarehouse,
    userRoles,
    userWarehouses,
    snowflakeAuth,
    snowflakeOauth,
  }
})
